












































































































/* eslint-disable no-underscore-dangle */
import Vue from 'vue';
import Layout from '@/layouts/_DefaultLayoutWithCard.vue';
import TrajectoryResultChart from '@/use-cases/red-mat-calculation/TrajectoryResultChart';
import TrajectoryResultHistogram from '@/use-cases/red-mat-calculation/TrajectoryResultHistogram';
import CalculateResponse from '../../use-cases/red-mat-calculation/CalculateResponse';
import RedMatCalculationTypes from '../../use-cases/red-mat-calculation/RedMatCalculationTypes';

export default Vue.extend({
  name: 'RedMatCalculationResults',
  components: {
    Layout,
    TrajectoryResultChart,
    TrajectoryResultHistogram,
  },
  data() {
    return {
      headers: [
        {
          text: 'Atom Number',
          value: 'atomNumber',
        },
        {
          text: 'Atom Name',
          value: 'atomName',
        },
        {
          text: 'Residue Number',
          value: 'residueNumber',
        },
        {
          text: 'Residue Name',
          value: 'residueName',
        },
        {
          text: 'Observed STD0',
          value: 'observed',
        },
        {
          text: 'Predicted STD0',
          value: 'predicted',
        },
      ],
      jsonDownloadLink: '',
      csvDownloadLink: '',
      avgEpitopeDownloadLink: '',
      movingAverageWindow: 20,
    };
  },
  computed: {
    calculationType(): RedMatCalculationTypes {
      return this.$store.state.redMatCalculation.calculationType;
    },
    isDocking() {
      return this.calculationType === RedMatCalculationTypes.Docking;
    },
    isMd() {
      return this.calculationType === RedMatCalculationTypes.MolecularDynamics;
    },
    result(): CalculateResponse {
      return this.$store.state.redMatCalculation.result;
    },
    ligandTrajectoryChartData() {
      const data = this.result.configurations.map((c) => c.rFactor.ligandRFactor);
      const movingAverage = this.movingAverage(data);

      return {
        labels: data.map((d, i) => i + 1),
        datasets: [
          {
            label: 'RNOE',
            data,
          },
          {
            label: 'Avg.',
            data: movingAverage,
            borderColor: '#FF0000',
          },
        ],
      };
    },
    trajectoryChartStyles() {
      return {
        position: 'relative',
        height: '50vh',
      };
    },
    trajectoryHistogramOptions() {
      return {
        maintainAspectRatio: false,
      };
    },
    trajectoryChartOptions() {
      return {
        maintainAspectRatio: false,
        onClick: (e, arr) => {
          if (arr.length > 0) {
            const configuration = this.result.configurations[arr[0]._index];
            const downloadName = `Frame${arr[0]._index + 1}`;
            this.createStd0CsvDownload(configuration, downloadName);
          }
        },
        tooltips: {
          callbacks: {
            afterLabel() {
              return 'Click to download STD0 values for frame.';
            },
          },
        },
      };
    },
    trajectoryHistogramData() {
      return {
        labels: this.result.histogram.labels,
        datasets: [
          {
            label: 'RNOE',
            data: this.result.histogram.values,
          },
        ],
      };
    },
  },
  methods: {
    residueHasData(residueNumber: number) {
      return this.result.configurations[0].stds
        .filter((x) => x.residueNumber === residueNumber)
        .some((x) => x.observed > Number.EPSILON);
    },
    atomHasData(atomNumber: number) {
      return this.result.configurations[0].stds
        .filter((x) => x.atomNumber === atomNumber)
        .every((x) => x.observed > Number.EPSILON);
    },
    residues() {
      return this.result.configurations[0].rFactor.residueRFactors
        .map((x) => ({
          residueNumber: x.residueNumber,
          residueName: x.residueName,
        }))
        .filter((x) => this.residueHasData(x.residueNumber));
    },
    residueTrajectoryChartData(residueNumber: number) {
      const data = this.result.configurations.map((c) => c.rFactor.residueRFactors);

      const dataset = [];
      data.forEach((d) => {
        d.forEach((e, i) => {
          if (e.residueNumber === residueNumber) {
            dataset.push(e.rFactor);
          }
        });
      });

      const movingAverage = this.movingAverage(dataset);

      return {
        labels: data.map((d, i) => i + 1),
        datasets: [
          {
            label: residueNumber,
            data: dataset,
          },
          {
            label: 'Avg.',
            data: movingAverage,
            borderColor: '#FF0000',
          },
        ],
      };
    },
    residueTrajectoryHistogramData(residueNumber: number) {
      const residue = this.result.histogram.residues
        .filter((x) => x.residueNumber === residueNumber)[0];

      return {
        labels: residue.labels,
        datasets: [
          {
            label: `RNOE (Residue ${residue.residueNumber})`,
            data: residue.values,
          },
        ],
      };
    },
    atomsByResidue(residueNumber: number) {
      return this.result.configurations[0].stds
        .filter((x) => x.residueNumber === residueNumber)
        .map((x) => ({
          atomNumber: x.atomNumber,
          atomName: x.atomName,
        }))
        .filter((x) => this.atomHasData(x.atomNumber));
    },
    atomTrajectoryChartData(atomNumber: number) {
      const data = this.result.configurations.map((c) => c.rFactor.atomRFactors);

      const dataset = [];
      data.forEach((d) => {
        d.forEach((e, i) => {
          if (e.atomNumber === atomNumber) {
            dataset.push(e.rFactor);
          }
        });
      });

      const movingAverage = this.movingAverage(dataset);

      return {
        labels: data.map((d, i) => i + 1),
        datasets: [
          {
            label: atomNumber,
            data: dataset,
          },
          {
            label: 'Avg.',
            data: movingAverage,
            borderColor: '#FF0000',
          },
        ],
      };
    },
    movingAverage(array) {
      const result = [];

      for (let i = 0; i < array.length; i += 1) {
        const subArr = array.slice(
          Math.max(i - this.movingAverageWindow, 0),
          Math.min(i + 1, array.length),
        );

        const avg = subArr.reduce((a, b) => a + b, 0) / subArr.length;
        result.push(avg);
      }

      return result;
    },
    createStd0CsvDownload(configuration, downloadName) {
      let csv = 'Atom Number,Atom Name,Residue Number,Residue Name,Observed STD0,Predicted STD0\r\n';
      configuration.stds.forEach((std) => {
        const line = `${std.atomNumber},${std.atomName},${std.residueNumber},${std.residueName},${std.observed},${std.predicted}\r\n`;
        csv = csv.concat(line);
      });
      const csvUrl = URL.createObjectURL(new Blob([csv], {
        type: 'text/csv',
      }));

      const link = document.createElement('a');
      link.setAttribute('href', csvUrl);
      link.setAttribute('download', `${downloadName}.csv`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    onClickDownloadModelCsv(index) {
      this.createStd0CsvDownload(this.result.configurations[index], `Model ${index + 1}`);
    },
    createCsvLink(headers, values) {
      const csv = [
        headers,
        ...values.map((v) => Object.values(v)),
      ].map((e) => e.join()).join('\r\n');

      return URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    },
  },
  created() {
    // create calculation download link
    const json = JSON.stringify(this.result);
    const jsonUrl = URL.createObjectURL(new Blob([json], {
      type: 'text/json',
    }));
    this.jsonDownloadLink = jsonUrl;

    // create csv download link
    this.csvDownloadLink = this.createCsvLink(
      ['Frame', 'RNOE'],
      this.result.configurations.map((c) => ({
        title: c.configurationTitle,
        rFactor: c.rFactor.ligandRFactor,
      })),
    );

    // create average epitope
    const stdsAvg = this.result.configurations
      .reduce((agg, configuration) => {
        for (let i = 0; i < configuration.stds.length; i += 1) {
          const std = configuration.stds[i];
          const cumSum = agg[i] || {
            residueName: std.residueName,
            atomName: std.atomName,
            value: 0,
          };

          cumSum.value += std.predictedAbs;

          // eslint-disable-next-line no-param-reassign
          agg[i] = cumSum;
        }

        return agg;
      }, [])
      .map((sum) => ({
        ...sum,
        value: sum.value ? sum.value / this.result.configurations.length : 0,
      }));

    const max = Math.max(...stdsAvg.map((s) => s.value));
    const stdsNorm = stdsAvg.map((s) => ({ ...s, value: (s.value / max) * 100 }));
    this.avgEpitopeDownloadLink = this.createCsvLink(
      ['Residue', 'Atom', 'Avg. STD'],
      stdsNorm,
    );
  },
});
